import { useCallback, useEffect, useRef, useState } from "react";
import {
  useTallyConnectionFlow,
  type SyncPhase,
  type TallyTrackFn,
} from "../../Hooks/useTallyConnectionFlow";
import type { TallyAppConnectionStatus } from "../../../rest/gst-rest-service";
import type { TallyDashboardModalStage } from "./TallyDashboardSyncModal/TallyDashboardSyncModal";

/**
 * Optional side-effect callbacks the host page may supply. The hook owns the
 * modal's stage/failed/receivingStartedAt state machine; these callbacks let a
 * consumer react to phase transitions without re-implementing the state
 * machine itself (e.g. GST Recon kicks off dashboard processing, refetches
 * files, and clears recon timers). RevenueDetails / Lending pass none of them.
 */
export interface TallyDashboardFlowCallbacks {
  /** Fired when `phase` transitions to `connected` (Tally linked, sync starts). */
  onConnected?: () => void;
  /** Fired when `phase` transitions to `synced` (push completed). */
  onSynced?: () => void;
  /** Fired when `phase` transitions to `failed`. */
  onFailed?: () => void;
  /**
   * Called with the latest `TallyAppConnectionStatus` whenever the hook learns
   * it (from checkStatus or a socket event). Lets the host keep its own
   * `tallyAppConnection` state in sync without a separate API call.
   */
  onConnectionChange?: (conn: TallyAppConnectionStatus) => void;
}

/**
 * Drives `TallyDashboardSyncModal`'s stage/failed/receivingStartedAt props off
 * of `useTallyConnectionFlow`'s phase. The modal itself is presentational only
 * and needs a state machine feeding it. This hook is the single shared state
 * machine — used by both GST Reconciliation's dashboard reconnect modal and
 * Lending/Underwriting's RevenueDetails Tally connect.
 *
 * `initialConnection` lets a host that already fetched `tallyAppConnection`
 * during its own init (e.g. GSTReconciliationIndex) seed the resume state
 * without an extra `checkStatus` round-trip.
 */
export function useTallyDashboardFlow(
  onTrack?: TallyTrackFn,
  callbacks?: TallyDashboardFlowCallbacks,
  initialConnection?: TallyAppConnectionStatus | null,
) {
  const { phase, error, downloadTdl, checkStatus, retrySync } =
    useTallyConnectionFlow("idle", onTrack);

  const [isOpen, setIsOpen] = useState(false);
  const [stage, setStage] = useState<TallyDashboardModalStage>("setup");
  const [failed, setFailed] = useState(false);
  const [receivingStartedAt, setReceivingStartedAt] = useState<number | null>(
    null,
  );
  const [connection, setConnection] = useState<TallyAppConnectionStatus | null>(
    initialConnection ?? null,
  );

  // useState's initializer only runs once — without this, a host whose own
  // tallyAppConnection/tallyConnection updates later (a fresh fetch after a
  // sync completes, a socket-driven refresh) would leave `connection` here
  // stuck at whatever it was on first mount, since open() no longer re-fetches
  // it itself.
  useEffect(() => {
    if (initialConnection) setConnection(initialConnection);
  }, [initialConnection]);

  // Keep the latest callbacks in refs so the phase effect doesn't re-run when
  // the host's inline callbacks change identity.
  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  const lastHandledPhase = useRef<SyncPhase>(phase);

  useEffect(() => {
    if (lastHandledPhase.current === phase) return;
    lastHandledPhase.current = phase;

    if (phase === "connected") {
      setFailed(false);
      setStage("receiving");
      setReceivingStartedAt((startedAt) => startedAt ?? Date.now());
      cbRef.current?.onConnected?.();
    } else if (phase === "synced") {
      setStage("success");
      cbRef.current?.onSynced?.();
    } else if (phase === "failed") {
      setFailed(true);
      setStage("install");
      setReceivingStartedAt(null);
      cbRef.current?.onFailed?.();
    }
  }, [phase]);

  // Sync local `connection` and notify host whenever we learn the status.
  const applyConnection = useCallback(
    (conn: TallyAppConnectionStatus) => {
      setConnection(conn);
      cbRef.current?.onConnectionChange?.(conn);
    },
    [],
  );

  // Callers wire this directly as an onClick handler (`onClick={tallyFlow.open}`).
  // No network call here — `connection` is seeded once from the host page's own
  // fetch (`initialConnection`, e.g. RevenueDetails' UnderwritingFormContext
  // status call on mount) and kept current by live socket events via `phase`,
  // so re-fetching status on every click would just be a redundant round-trip
  // for data the page already has. `tallySynced` (recent push, not just ever
  // having paired) takes priority so a stale-but-connected pairing resumes
  // into "success" rather than the misleading "receiving"/syncing spinner.
  const open = useCallback(() => {
    const isConnected = Boolean(connection?.tdlDownloaded && connection?.tallyConnected);
    const isSynced = isConnected && Boolean(connection?.tallySynced);
    const isFailed = !isConnected && Boolean(connection?.tdlDownloaded && connection?.tallySyncFailed);

    setFailed(isFailed);
    if (isSynced) {
      setStage("success");
      setReceivingStartedAt(null);
    } else if (isFailed) {
      setStage("install");
      setReceivingStartedAt(null);
    } else if (isConnected) {
      setStage("receiving");
      setReceivingStartedAt((startedAt) => startedAt ?? Date.now());
    } else {
      setStage("setup");
      setReceivingStartedAt(null);
    }
    setIsOpen(true);
  }, [connection]);

  // "Resync Tally" variant — always starts from the download/setup step,
  // ignoring any prior failed state. Used by the "Connect Tally" banner action
  // and cases where Tally isn't fully connected yet.
  const openSetup = useCallback(() => {
    setStage("setup");
    setFailed(false);
    setReceivingStartedAt(null);
    lastHandledPhase.current = phase;
    setIsOpen(true);
  }, [phase]);

  // "Resync Tally" variant for already-connected Tally — skips the TDL
  // download/setup step and opens directly at install, since the TDL is
  // already downloaded and the user just needs to re-run the sync.
  const openInstall = useCallback(() => {
    setStage("install");
    setFailed(false);
    setReceivingStartedAt(null);
    lastHandledPhase.current = phase;
    setIsOpen(true);
  }, [phase]);

  const close = useCallback(() => {
    setIsOpen(false);
    if (stage === "success") {
      setStage("setup");
      setFailed(false);
      setReceivingStartedAt(null);
    }
  }, [stage]);

  const continueToInstall = useCallback(() => {
    setStage("install");
  }, []);

  const back = useCallback(() => {
    setStage("setup");
  }, []);

  // TallyDashboardSyncModal's onDownload contract is () => Promise<void> —
  // downloadTdl resolves with the TDL response, so wrap it to discard that.
  const downloadTdlForModal = useCallback(async () => {
    await downloadTdl();
    if (connection) {
      applyConnection({
        ...connection,
        tdlDownloaded: true,
        tallySyncFailed: connection.tallySyncFailed ?? false,
      });
    }
  }, [applyConnection, connection, downloadTdl]);

  return {
    isOpen,
    isConnected: phase === "connected" || phase === "synced",
    /** Current `TallyAppConnectionStatus` (null until first checkStatus/socket). */
    connection,
    stage,
    failed,
    receivingStartedAt,
    error,
    open,
    openSetup,
    openInstall,
    close,
    continueToInstall,
    back,
    downloadTdl: downloadTdlForModal,
    retrySync,
    /** Imperative status refresh — also updates `connection`. */
    checkStatus,
  };
}

export type TallyDashboardFlowResult = ReturnType<typeof useTallyDashboardFlow>;
